/**
 * VennDiagramSVG — Phase 1, Component 8 of the TeachBud Representation Catalog
 *
 * Renders a Venn diagram (two overlapping circles) per §5.2 Compare & Contrast:
 *   - Grade 2–3: two large circles, labeled; student writes in each region
 *   - Grade 4–5: slightly smaller; includes "Both" label in the overlap
 *   - Grade 6–8: may be three circles for complex comparisons (future)
 *   - Student mode: empty regions with ruled lines for writing
 *   - Teacher mode: content provided via `leftItems`, `rightItems`, `bothItems`
 */

import React from 'react';

export interface VennDiagramProps {
  /** Label for the left circle. */
  leftLabel?: string;
  /** Label for the right circle. */
  rightLabel?: string;
  /** Label for the overlap region. Default "Both". */
  bothLabel?: string;
  /** Content for the left-only region. */
  leftItems?: string[];
  /** Content for the right-only region. */
  rightItems?: string[];
  /** Content for the overlap region. */
  bothItems?: string[];
  /** When true, regions are empty with ruled lines. */
  studentMode?: boolean;
  /** Override pixel width. */
  width?: number;
  /** Grade (0=K). Adjusts sizing. */
  grade?: number;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W = 560;
const VB_H = 320;

const CX1 = 195;
const CX2 = 365;
const CY  = 175;
const R   = 155;
const OVERLAP_CX = (CX1 + CX2) / 2;

const LEFT_FILL   = '#EFF6FF';  // blue-50
const RIGHT_FILL  = '#F5F3FF';  // violet-50
const BOTH_FILL   = '#E0E7FF';  // indigo-100
const STROKE      = '#6B7280';
const LABEL_COLOR = '#1E3A5F';
const LINE_COLOR  = '#D1D5DB';

export function VennDiagramSVG({
  leftLabel  = 'Topic A',
  rightLabel = 'Topic B',
  bothLabel  = 'Both',
  leftItems  = [],
  rightItems = [],
  bothItems  = [],
  studentMode = true,
  width,
  grade = 3,
  className = '',
}: VennDiagramProps) {
  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;
  const LABEL_FS = grade <= 2 ? 16 : grade <= 4 ? 14 : 12;
  const ITEM_FS  = grade <= 2 ? 12 : 10;

  // Ruled lines for each region in student mode
  const linesInRegion = grade <= 2 ? 3 : 4;
  const leftLineX  = CX1 - R + 20;
  const rightLineX = CX2 - R / 2 + 10;
  const overlapX   = OVERLAP_CX;

  function regionLines(regionX: number, regionY: number, nLines: number, lineW: number) {
    return Array.from({ length: nLines }).map((_, i) => (
      <line
        key={i}
        x1={regionX - lineW / 2} y1={regionY + i * 22}
        x2={regionX + lineW / 2} y2={regionY + i * 22}
        stroke={LINE_COLOR} strokeWidth={1}
      />
    ));
  }

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW} height={svgH}
      className={className}
      role="img"
      aria-label={`Venn diagram comparing ${leftLabel} and ${rightLabel}`}
    >
      <title>Venn diagram: {leftLabel} vs {rightLabel}</title>

      <defs>
        <clipPath id="leftOnly">
          <circle cx={CX1} cy={CY} r={R} />
        </clipPath>
        <clipPath id="rightOnly">
          <circle cx={CX2} cy={CY} r={R} />
        </clipPath>
      </defs>

      {/* Left-only fill */}
      <circle cx={CX1} cy={CY} r={R} fill={LEFT_FILL} stroke="none" />
      {/* Right-only fill */}
      <circle cx={CX2} cy={CY} r={R} fill={RIGHT_FILL} stroke="none" />
      {/* Overlap fill — drawn on top via two clipped rects */}
      <rect x={CX1} y={CY - R} width={CX2 - CX1} height={R * 2} fill={BOTH_FILL} clipPath="url(#leftOnly)" />
      <rect x={CX1} y={CY - R} width={CX2 - CX1} height={R * 2} fill={BOTH_FILL} clipPath="url(#rightOnly)" />

      {/* Circle outlines */}
      <circle cx={CX1} cy={CY} r={R} fill="none" stroke={STROKE} strokeWidth={2} />
      <circle cx={CX2} cy={CY} r={R} fill="none" stroke={STROKE} strokeWidth={2} />

      {/* Labels at top of each circle */}
      <text x={CX1 - 40} y={CY - R + 24} textAnchor="middle" fontSize={LABEL_FS} fontFamily="Nunito, sans-serif" fontWeight={700} fill={LABEL_COLOR}>
        {leftLabel}
      </text>
      <text x={CX2 + 40} y={CY - R + 24} textAnchor="middle" fontSize={LABEL_FS} fontFamily="Nunito, sans-serif" fontWeight={700} fill={LABEL_COLOR}>
        {rightLabel}
      </text>
      {/* Overlap label (default "Both"; teacher-editable via bothLabel prop) */}
      <text x={OVERLAP_CX} y={CY - R + 24} textAnchor="middle" fontSize={LABEL_FS - 2} fontFamily="Nunito, sans-serif" fontWeight={600} fill="#6366F1">
        {bothLabel}
      </text>

      {/* Student mode: ruled lines */}
      {studentMode && (
        <>
          {regionLines(CX1 - 55, CY - 40, linesInRegion, 90)}
          {regionLines(OVERLAP_CX, CY - 40, linesInRegion, 70)}
          {regionLines(CX2 + 55, CY - 40, linesInRegion, 90)}
        </>
      )}

      {/* Teacher mode: content items */}
      {!studentMode && leftItems.map((item, i) => (
        <text key={i} x={CX1 - 55} y={CY - 30 + i * 22} textAnchor="middle" fontSize={ITEM_FS} fontFamily="Inter, sans-serif" fill={LABEL_COLOR}>
          {item}
        </text>
      ))}
      {!studentMode && bothItems.map((item, i) => (
        <text key={i} x={OVERLAP_CX} y={CY - 30 + i * 22} textAnchor="middle" fontSize={ITEM_FS} fontFamily="Inter, sans-serif" fill={LABEL_COLOR}>
          {item}
        </text>
      ))}
      {!studentMode && rightItems.map((item, i) => (
        <text key={i} x={CX2 + 55} y={CY - 30 + i * 22} textAnchor="middle" fontSize={ITEM_FS} fontFamily="Inter, sans-serif" fill={LABEL_COLOR}>
          {item}
        </text>
      ))}
    </svg>
  );
}

// ── Hint parser ───────────────────────────────────────────────────────────────
export function parseVennDiagramHint(hint: string): VennDiagramProps {
  // "venn diagram: frogs and toads" → extract the two labels
  const labels = hint.match(/venn[^:]*:\s*([^a][^\n]+?)\s+and\s+(.+)/i);
  if (labels) {
    return {
      leftLabel: labels[1].trim().slice(0, 20),
      rightLabel: labels[2].trim().slice(0, 20),
      studentMode: true,
    };
  }
  return { leftLabel: 'Topic A', rightLabel: 'Topic B', studentMode: true };
}
